import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    Box,
    List,
    Typography,
    IconButton,
    InputBase,
    Button,
    CircularProgress,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useDeepCompareMemo, useDeepCompareEffect } from 'use-deep-compare';
import { useNavigate } from 'react-router-dom';
import { Search, Close, EditNote } from '@mui/icons-material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { MessageDialogue } from '../../../components/MessageDialogue/MessageDialogue';
import { ROUTES } from '../../../types/global/routes.types';
import { useMessageContext } from '../../../hook/useMessageContext';
import type { IRoom, IPagination } from '../../../redux/messages/types';
import RoomItem from '../../../components/RoomItem/RoomItem';
import RoomItemSkeleton from '../../../components/RoomItem/RoomItemSkeleton';
import { useDispatch, useSelector } from 'react-redux';
import { userHasReadMessage, searchRooms, getMyRooms } from '../../../redux/messages/actions';
import { motion, AnimatePresence } from 'framer-motion';
import { getJWtDetails } from '../../../utils/AppUtils';
import { debounce } from 'lodash';
import { fetchingRoomsSelector, searchingSelector } from '../../../selectors/selectors';

interface IConversationsListProps {
    rooms: IRoom[];
    activeRoomId: string | null;
    onRoomClicked?: (roomId: string) => void;
    pagination: IPagination;
}

function participantUserNameLower(user: IRoom['sender'] | IRoom['receiver'][number]): string {
    if (!user || typeof user === 'string') return '';
    return (user.userName || '').toLowerCase();
}

/** Official Thspian account (support) — may appear as sender or receiver depending on room shape. */
function isThspianOfficialRoom(room: IRoom): boolean {
    if (participantUserNameLower(room.sender) === 'thspian') return true;
    return Boolean(room.receiver?.some(r => participantUserNameLower(r) === 'thspian'));
}

const ConversationsList: React.FC<IConversationsListProps> = ({ rooms, activeRoomId, onRoomClicked, pagination }) => {
    const { themeStyle } = useThemeContext();
    const muiTheme = useTheme();
    const compactHeader = useMediaQuery(muiTheme.breakpoints.down('md'));
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [newMessageOpen, setNewMessageOpen] = useState(false);
    const { setMessage } = useMessageContext();
    const [loadingMore, setLoadingMore] = useState(false);
    const { user_id } = getJWtDetails();

    // Add search state
    const [searchTerm, setSearchTerm] = useState('');
    const isSearching = useSelector(searchingSelector);
    const isLoading = useSelector(fetchingRoomsSelector);

    const previousTimestampsRef = useRef<Record<string, string>>({});
    const updatedRoomIds = useRef<Set<string>>(new Set());

    const sortedRooms = useDeepCompareMemo(() => {
        return [...rooms].sort((a, b) => {
            const aIsThspian = isThspianOfficialRoom(a);
            const bIsThspian = isThspianOfficialRoom(b);

            // Pin official Thspian room (e.g. support) to the very top
            if (aIsThspian && !bIsThspian) return -1;
            if (!aIsThspian && bIsThspian) return 1;

            // Fallback: sort by last_message_at (newer first)
            const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
            const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;

            return bTime - aTime;
        });
    }, [rooms]);

    useDeepCompareEffect(() => {
        const newTimestamps: Record<string, string> = {};
        const updatedIds = new Set<string>();

        for (const room of rooms) {
            const prevTimestamp = previousTimestampsRef.current[room._id];
            const newTimestamp = room.last_message_at;

            if (prevTimestamp && prevTimestamp !== newTimestamp) {
                updatedIds.add(room._id);
            }
            newTimestamps[room._id] = newTimestamp;
        }

        previousTimestampsRef.current = newTimestamps;
        updatedRoomIds.current = updatedIds;
    }, [sortedRooms, rooms]);

    const handleRoomClick = (roomId: string) => {
        setMessage('');
        onRoomClicked?.(roomId);
        handleUserRead(roomId);
        navigate(`${ROUTES.MESSAGES}/${roomId}`);
    };

    const handleNewMessage = () => {
        setNewMessageOpen(true);
    };

    const handleLoadRooms = useCallback(() => {
        dispatch(getMyRooms({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        handleLoadRooms();
    }, [handleLoadRooms]);

    const handleLoadMore = useCallback(() => {
        if (!pagination.hasNextPage || loadingMore) return;

        setLoadingMore(true);

        // Use search or regular load based on whether we're in search mode
        if (searchTerm) {
            dispatch(
                searchRooms({
                    searchTerm,
                    page: pagination.currentPage + 1,
                    limit: pagination.limit,
                }),
            );
        } else {
            dispatch(
                getMyRooms({
                    limit: 10,
                    page: pagination.currentPage + 1,
                }),
            );
        }

        setLoadingMore(false);
    }, [pagination, dispatch, loadingMore, searchTerm]);

    const handleUserRead = (room: string) => {
        dispatch(
            userHasReadMessage({
                readBy: user_id,
                roomID: room,
            }),
        );
    };

    // Debounced search handler
    const debouncedSearch = useRef(
        debounce((term: string) => {
            if (term.trim() === '') {
                handleLoadRooms(); // Reset to all rooms when search is cleared
            } else {
                // Only dispatch search if we're not already searching
                if (!isSearching) {
                    dispatch(
                        searchRooms({
                            searchTerm: term,
                            limit: pagination.limit,
                        }),
                    );
                }
            }
        }, 500),
    ).current;

    // Search input handler
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    // Clear search
    const handleClearSearch = () => {
        setSearchTerm('');
        handleLoadRooms();
    };

    const headerStickyBg = theme[themeStyle].deep_grey;

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
            {/* Sticky top: title, search, new message */}
            <Box
                sx={{
                    flexShrink: 0,
                    zIndex: 10,
                    p: compactHeader ? 2 : 3,
                    pb: compactHeader ? 2 : 2.5,
                    borderBottom: `1px solid ${alpha(theme[themeStyle].grey, 0.12)}`,
                    bgcolor: headerStickyBg,
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: compactHeader ? 1 : 1.5,
                        mb: 2,
                        flexWrap: 'nowrap',
                    }}>
                    <Typography
                        component="h1"
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: theme[themeStyle].tag_white,
                            fontSize: compactHeader ? '1.2rem' : '1.5rem',
                            fontWeight: 800,
                            letterSpacing: '-0.04em',
                            lineHeight: 1.1,
                            minWidth: 0,
                        }}>
                        Messages
                    </Typography>
                    <Button
                        onClick={handleNewMessage}
                        startIcon={
                            <EditNote
                                sx={{
                                    fontSize: compactHeader ? '0.875rem !important' : '0.9375rem !important',
                                }}
                            />
                        }
                        sx={{
                            flexShrink: 0,
                            py: compactHeader ? 0.55 : 0.85,
                            px: compactHeader ? 1.35 : 1.75,
                            minHeight: 0,
                            borderRadius: 999,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 700,
                            textTransform: 'none',
                            fontSize: compactHeader ? '0.72rem' : '0.8125rem',
                            gap: compactHeader ? 0.35 : 0.5,
                            bgcolor: theme[themeStyle].true_highlight,
                            color: theme[themeStyle].true_black,
                            whiteSpace: 'nowrap',
                            '&:hover': {
                                opacity: 0.9,
                                bgcolor: theme[themeStyle].true_highlight,
                            },
                            transition: 'opacity 0.2s ease, transform 0.12s ease',
                            '&:active': {
                                transform: 'scale(0.97)',
                            },
                        }}>
                        New Message
                    </Button>
                </Box>

                <Box sx={{ position: 'relative', mb: 0 }}>
                    <Search
                        sx={{
                            position: 'absolute',
                            left: 12,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: 18,
                            color: theme[themeStyle].grey,
                            pointerEvents: 'none',
                        }}
                    />
                    <InputBase
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search talents..."
                        sx={{
                            width: '100%',
                            pl: '40px',
                            pr: searchTerm ? 4 : 1.5,
                            py: 1.25,
                            borderRadius: '12px',
                            fontSize: '0.875rem',
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            color: theme[themeStyle].tag_white,
                            bgcolor:
                                themeStyle === 'light'
                                    ? alpha(theme[themeStyle].true_black, 0.04)
                                    : alpha(theme[themeStyle].true_black, 0.35),
                            border: 'none',
                            transition: 'box-shadow 0.15s ease',
                            '&.Mui-focused': {
                                boxShadow: `0 0 0 1px ${alpha(theme[themeStyle].highlight, 0.35)}`,
                            },
                        }}
                    />
                    {searchTerm && (
                        <IconButton
                            size="small"
                            onClick={handleClearSearch}
                            aria-label="Clear search"
                            sx={{
                                position: 'absolute',
                                right: 6,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: theme[themeStyle].grey,
                            }}>
                            <Close fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Conversation list (search loading only here — not in the search bar) */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    bgcolor: 'transparent',
                }}>
                {searchTerm.trim() !== '' && isSearching ? (
                    <Box
                        role="status"
                        aria-live="polite"
                        aria-label="Searching conversations"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            py: 6,
                            px: 2,
                            minHeight: 200,
                        }}>
                        <CircularProgress size={40} sx={{ color: theme[themeStyle].highlight }} />
                        <Typography
                            sx={{
                                color: theme[themeStyle].grey,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '0.875rem',
                            }}>
                            Searching talents…
                        </Typography>
                    </Box>
                ) : isLoading && sortedRooms.length === 0 ? (
                    <List sx={{ p: 0 }}>
                        {Array(6)
                            .fill(0)
                            .map((_, index) => (
                                <RoomItemSkeleton key={`skeleton-${index}`} showDivider />
                            ))}
                    </List>
                ) : sortedRooms.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].grey,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}>
                            {searchTerm ? 'No conversations match your search' : 'No conversations yet'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <List sx={{ p: 0 }}>
                            <AnimatePresence initial={false}>
                                {sortedRooms.map(room => (
                                    <motion.div
                                        key={room._id}
                                        layout
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.25 }}
                                        style={{
                                            overflow: 'hidden',
                                            backgroundColor: updatedRoomIds.current.has(room._id)
                                                ? alpha(theme[themeStyle].highlight, 0.12)
                                                : 'transparent',
                                        }}>
                                        <RoomItem
                                            onClick={() => handleRoomClick(room._id)}
                                            room={room}
                                            isActive={activeRoomId === room._id}
                                            showDivider={false}
                                        />
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </List>

                        {pagination.hasNextPage && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Button
                                    onClick={handleLoadMore}
                                    disabled={loadingMore}
                                    sx={{
                                        color: theme[themeStyle].highlight,
                                        '&:hover': {
                                            bgcolor: alpha(theme[themeStyle].tag_white, 0.06),
                                        },
                                    }}>
                                    {loadingMore ? (
                                        <CircularProgress size={24} sx={{ color: theme[themeStyle].true_white }} />
                                    ) : (
                                        'Load More'
                                    )}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {newMessageOpen && (
                <MessageDialogue open={newMessageOpen} fromModal onClose={() => setNewMessageOpen(false)} />
            )}
        </Box>
    );
};

export default ConversationsList;
